/**
 * src/data/InterviewMasteryBank.js
 * Hardcoded scenario bank for Module 2: Interview Mastery (Adaptive Adversary)
 */

export const interviewMasteryBank = [
  {
    "id": "interview_01",
    "scenarioContext": "You are explaining a successful product launch using the STAR method. You are halfway through the 'Action' phase.",
    "adversaryPrompt": "INTERVIEWER: 'Let me stop you right there. I don't care about the marketing launch. If your project lead quit two weeks before delivery, how specifically did you reallocate their critical tasks?'",
    "options": [
      {
        "text": "I immediately reviewed the outstanding work, deferred two non-critical features to the next quarter, and redistributed the core tasks among the remaining team members based on their current workload.",
        "category": "Optimal",
        "feedback": "Correct. Flawless dynamic pivot. You dropped the marketing script instantly and answered the exact operational challenge presented."
      },
      {
        "text": "Well, as I was saying about the marketing launch, we had a great team effort. But to answer your question, we just worked harder to cover the missing project lead's work.",
        "category": "Script Reversion",
        "feedback": "Failure. You clung to your obsolete script and provided a generic, non-operational answer to a highly specific technical question."
      },
      {
        "text": "I synergized our cross-functional bandwidth to leverage agile methodologies, ensuring our paradigm shift aligned with the core KPIs.",
        "category": "Jargon Panic",
        "feedback": "Failure. Word-vomit. You panicked under the interruption and threw empty corporate jargon at the interviewer instead of actionable data."
      },
      {
        "text": "You didn't let me finish my previous point. But I guess I would just tell the team to figure it out.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Becoming defensive and antagonistic when an executive interrupts your narrative is an immediate disqualifier."
      }
    ]
  },
  {
    "id": "interview_02",
    "scenarioContext": "You are presenting an example of how you used data analytics to solve a supply chain issue, focusing on the high-level dashboard you built.",
    "adversaryPrompt": "INTERVIEWER: 'Hold on. Dashboards are just pretty pictures. What was the specific statistical variance in the delayed shipments, and how did you handle the data outliers?'",
    "options": [
      {
        "text": "The variance was 14% on standard deviations. For the outliers—specifically shipments delayed over 30 days due to port strikes—we isolated the extreme outliers so they wouldn't skew the final financial projections.",
        "category": "Optimal",
        "feedback": "Correct. You immediately abandoned the high-level fluff and delivered the hard, clinical statistical methodology requested."
      },
      {
        "text": "The dashboard was actually highly functional and praised by the VP. But regarding the outliers, we basically just looked at the highest numbers and excluded them to keep the chart clean.",
        "category": "Script Reversion",
        "feedback": "Failure. You wasted time defending the dashboard and then revealed a lack of actual statistical depth."
      },
      {
        "text": "We leveraged big data lakes to synergize our predictive algorithms, optimizing the supply chain matrix for maximum operational throughput.",
        "category": "Jargon Panic",
        "feedback": "Failure. You used 'big data' and 'algorithms' to mask the fact that you do not know how to handle statistical outliers."
      },
      {
        "text": "I wasn't prepared to do a deep-dive math test today. I'm a manager, not an analyst. My team handles the raw data.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Abdicating responsibility to your team when pressed for details reveals you lack hands-on systemic understanding."
      }
    ]
  },
  {
    "id": "interview_03",
    "scenarioContext": "You are answering a standard 'Tell me about a time you had a conflict with a coworker' question. You are setting up the background.",
    "adversaryPrompt": "INTERVIEWER: 'Skip the background. What if that coworker was actually your VP, and they just threatened to fire you for disagreeing? What are the exact next words out of your mouth?'",
    "options": [
      {
        "text": "'I respect your authority on this, but my concern stems from the compliance risk it poses to the company. Can we review the liability data together before making a final call?'",
        "category": "Optimal",
        "feedback": "Correct. You demonstrated extreme emotional intelligence. You de-escalated, acknowledged hierarchy, but firmly anchored your pushback in corporate risk."
      },
      {
        "text": "Well, in my original story, it was just a peer in marketing. But if it was my VP, I would sit down with them, use active listening, and find a compromise so we could both be happy.",
        "category": "Script Reversion",
        "feedback": "Failure. You clumsily referenced your original script and offered a naive, HR-textbook response to an executive threat."
      },
      {
        "text": "I would escalate an aligned holistic strategy to HR, ensuring we synergize our communication bandwidth to avoid a toxic paradigm.",
        "category": "Jargon Panic",
        "feedback": "Failure. Spewing buzzwords when faced with executive hostility shows a complete lack of genuine communication skills."
      },
      {
        "text": "If a VP threatened me like that, I would tell them I'm calling my lawyer and walk right out of the room.",
        "category": "Defensive Freeze",
        "feedback": "Failure. You took the bait and became instantly combative, failing the pressure test entirely."
      }
    ]
  },
  {
    "id": "interview_04",
    "scenarioContext": "You are detailing how you closed the biggest sales deal of your career and are about to explain your brilliant closing technique.",
    "adversaryPrompt": "INTERVIEWER: 'Cut the closing pitch. I want to know your exact fallback position if the client demanded a 40% discount right there on the spot, or they walk.'",
    "options": [
      {
        "text": "My walk-away threshold was 20%. If they demanded 40%, I would pivot to stripping out premium service levels and dedicated onboarding support to match their price without compromising our core profit margin.",
        "category": "Optimal",
        "feedback": "Correct. You demonstrated clinical negotiation skills, instantly defending your margin by trading scope for price."
      },
      {
        "text": "Like I was saying, my closing technique usually prevents that. But if they did, I would just remind them of our great value and hope they see that we are worth the full price.",
        "category": "Script Reversion",
        "feedback": "Failure. You stubbornly clung to your 'brilliant' close and offered 'hope' instead of a hard financial fallback strategy."
      },
      {
        "text": "I would leverage a win-win paradigm to synergize our value proposition, optimizing the discount structure to maximize downstream ROI.",
        "category": "Jargon Panic",
        "feedback": "Failure. You threw buzzwords at a direct financial question. This indicates you do not understand margin defense."
      },
      {
        "text": "That's an unrealistic scenario. Nobody asks for 40% at the final close if you've done your job right.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Arguing with the interviewer about the validity of the scenario shows arrogance and inflexibility."
      }
    ]
  },
  {
    "id": "interview_05",
    "scenarioContext": "You are answering a question about your leadership style, leaning heavily on being a 'servant leader' who always supports the team.",
    "adversaryPrompt": "INTERVIEWER: 'Stop. Everyone says they're a servant leader. Give me one highly specific example where your so-called servant leadership actively caused a project to fail.'",
    "options": [
      {
        "text": "Early in my career, I shielded a struggling team member from criticism to 'support' them. Because I didn't enforce harsh performance standards, they missed a core software deadline, delaying our launch by a month.",
        "category": "Optimal",
        "feedback": "Correct. You exhibited high self-awareness and vulnerability, instantly delivering a concrete example of a leadership failure without flinching."
      },
      {
        "text": "As a servant leader, my team usually thrives. I wouldn't say it ever caused a project to fail, but sometimes I care too much and work too late helping them.",
        "category": "Script Reversion",
        "feedback": "Failure. You gave a cliché 'my biggest weakness is I work too hard' answer, dodging the direct challenge entirely."
      },
      {
        "text": "I over-indexed on consensus-building bandwidth, which disrupted our agile velocity and misaligned the cross-functional deliverables.",
        "category": "Jargon Panic",
        "feedback": "Failure. You tried to hide a failure behind thick corporate jargon, signaling a lack of genuine accountability."
      },
      {
        "text": "I don't appreciate the implication. My leadership style has never caused a project to fail.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Extreme defensiveness and a refusal to admit fault. Lethal combination for an executive candidate."
      }
    ]
  },
  {
    "id": "interview_06",
    "scenarioContext": "You are boasting about a major cost-saving initiative where you slashed vendor costs by $100k for your department.",
    "adversaryPrompt": "INTERVIEWER: 'Wait. You saved $100k by switching vendors, but did the cheaper vendor cut corners on security? How exactly did you audit their security protocols before signing?'",
    "options": [
      {
        "text": "Before signing, I required them to submit their third-party compliance audit and had our internal IT security team run a rigorous stress test on their systems to ensure data safety.",
        "category": "Optimal",
        "feedback": "Correct. You immediately provided the precise technical and compliance frameworks used to mitigate the risk of your cost-cutting."
      },
      {
        "text": "The main goal was the $100k savings, which the CFO loved. But yes, I did ask them if they were secure and they assured me their systems were state-of-the-art.",
        "category": "Script Reversion",
        "feedback": "Failure. You tried to get back to your CFO boast while admitting you took a vendor's word on security. Dangerous liability."
      },
      {
        "text": "We deployed a holistic vendor-alignment matrix to synergize their bandwidth with our risk-management paradigm.",
        "category": "Jargon Panic",
        "feedback": "Failure. Meaningless corporate word-vomit. You clearly bypassed security protocols and are trying to hide it."
      },
      {
        "text": "That wasn't my job. Procurement and IT should have checked that before I signed the contract.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Passing the buck. As the initiative owner, deflecting responsibility for vendor risk is unacceptable."
      }
    ]
  },
  {
    "id": "interview_07",
    "scenarioContext": "You are describing your meticulous time-management system and how you never miss a deadline.",
    "adversaryPrompt": "INTERVIEWER: 'I don't care about your to-do apps. If the CEO and your biggest $5M client both demand a conflicting, massive deliverable at 5 PM today, who fails? Pick one.'",
    "options": [
      {
        "text": "I inform the CEO that I am prioritizing the $5M client's deliverable to protect company revenue, and I request the CEO's permission to deliver their internal report at 9 AM tomorrow.",
        "category": "Optimal",
        "feedback": "Correct. You made a hard, decisive choice based on revenue protection (the client) while actively managing up to the CEO."
      },
      {
        "text": "Well, with my time management system, I would just work through lunch and make sure both get done by 5 PM.",
        "category": "Script Reversion",
        "feedback": "Failure. You refused the parameters of the stress test, clinging to your script that you 'never miss a deadline.' Naive and unrealistic."
      },
      {
        "text": "I would synergize the deliverables by leveraging asynchronous bandwidth to optimize the output for both stakeholders simultaneously.",
        "category": "Jargon Panic",
        "feedback": "Failure. Gibberish. You froze under the pressure of choosing between two authorities and spewed buzzwords."
      },
      {
        "text": "It's unfair to put me in a situation where I have to anger either the CEO or a client. That's a sign of bad company culture.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Whining about the hypothetical scenario shows a complete inability to handle executive-level pressure."
      }
    ]
  },
  {
    "id": "interview_08",
    "scenarioContext": "You are answering a question about problem-solving and are explaining a brilliant strategic solution you devised.",
    "adversaryPrompt": "INTERVIEWER: 'Let's pivot. Assume your brilliant solution failed miserably in month one and cost the company $50k. Walk me through your immediate next steps.'",
    "options": [
      {
        "text": "First, I immediately halt the failing initiative to stop the financial bleeding. Second, I pull the analytics to isolate the point of failure. Third, I present a post-mortem and an adjusted recovery plan to stakeholders.",
        "category": "Optimal",
        "feedback": "Correct. Clinical and composed. You outlined a rapid, three-step crisis management protocol without getting defensive about your 'brilliant' idea."
      },
      {
        "text": "Well, the solution I implemented actually increased revenue by 20%. But if it did fail, I suppose I would just figure out what went wrong and try again.",
        "category": "Script Reversion",
        "feedback": "Failure. You couldn't let go of your success story, resulting in a weak, generic answer to a direct crisis scenario."
      },
      {
        "text": "I would leverage an agile pivot to synergize the learnings, optimizing our paradigm to align with the core KPIs.",
        "category": "Jargon Panic",
        "feedback": "Failure. Using empty jargon to describe a post-mortem shows you have no actual framework for handling failure."
      },
      {
        "text": "I wouldn't propose a solution that would fail that badly. I do my research beforehand.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Arrogant and inflexible. Refusing to entertain a hypothetical failure is a massive red flag for organizational adaptability."
      }
    ]
  },
  {
    "id": "interview_09",
    "scenarioContext": "You are describing your strategy for rolling out a new enterprise software platform to the entire company.",
    "adversaryPrompt": "INTERVIEWER: 'Cut the timeline stuff. A veteran team of top-performers flat-out refuses to log into your new software. Walk me through the exact conversation you have with their ringleader.'",
    "options": [
      {
        "text": "'I know this new interface slows down your current workflow. Walk me through your biggest friction point, and I will personally get the engineering team to build a custom shortcut for your department.'",
        "category": "Optimal",
        "feedback": "Correct. You bypassed corporate mandates and demonstrated acute emotional intelligence by trading a technical concession for their buy-in."
      },
      {
        "text": "As I mentioned in my rollout plan, we would have training sessions. I would remind the ringleader that training is mandatory and the software is required by management.",
        "category": "Script Reversion",
        "feedback": "Failure. You relied on your script's training plan and resorted to bureaucratic threats, which will alienate top performers."
      },
      {
        "text": "I would synergize with their pain points to leverage a win-win paradigm, ensuring our cross-functional alignment optimized their bandwidth.",
        "category": "Jargon Panic",
        "feedback": "Failure. If you speak to a veteran top-performer like this, they will instantly lose all respect for you."
      },
      {
        "text": "If they refuse to use company tools, I'd report them to HR. Insubordination can't be tolerated.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Escalating immediately to HR over a software dispute shows an utter lack of interpersonal leadership skills."
      }
    ]
  },
  {
    "id": "interview_10",
    "scenarioContext": "You are outlining your meticulously planned departmental budget for the upcoming fiscal year.",
    "adversaryPrompt": "INTERVIEWER: 'Hold on. Finance just slashed your budget by 50% halfway through Q3. Which specific headcount or software do you cut first, and why?'",
    "options": [
      {
        "text": "I cut our external marketing agency retainer immediately. It saves $40k a month in hard costs, and I can temporarily insource the core copywriting to my existing product team to protect headcount.",
        "category": "Optimal",
        "feedback": "Correct. You made a rapid, ruthless financial decision that protected your internal talent while sacrificing an external luxury."
      },
      {
        "text": "My budget plan had built-in contingencies. But if they cut 50%, I would just ask my team to cut back on travel and office expenses to make up the difference.",
        "category": "Script Reversion",
        "feedback": "Failure. You referenced your original plan, and then suggested cutting 'office expenses' to cover a massive 50% deficit. Mathematically naive."
      },
      {
        "text": "I would leverage an optimized fiscal paradigm, synergizing our core bandwidth to right-size the operational expenditures.",
        "category": "Jargon Panic",
        "feedback": "Failure. You hid behind buzzwords because you panicked at the thought of making a hard financial cut."
      },
      {
        "text": "A 50% cut mid-quarter means the company is failing. I'd probably start looking for a new job.",
        "category": "Defensive Freeze",
        "feedback": "Failure. While possibly true, saying this in an interview shows you flee at the first sign of organizational distress."
      }
    ]
  },
  {
    "id": "interview_11",
    "scenarioContext": "You are describing your ability to drive cross-functional alignment on a major product launch.",
    "adversaryPrompt": "INTERVIEWER: 'Right, but Legal just vetoed your entire project 24 hours before launch due to compliance. Do you go over their head to the CEO, or kill the launch?'",
    "options": [
      {
        "text": "Neither. I delay the launch by 48 hours, sit in a room with Legal to isolate the exact offending feature, disable just that module, and launch a compliant, stripped-down version of the product.",
        "category": "Optimal",
        "feedback": "Correct. You refused a false binary trap. You respected the legal boundary while finding a surgical technical workaround to salvage the launch."
      },
      {
        "text": "As I said, I build alignment early so this doesn't happen. But if it did, I would just kill the launch because Legal is always right.",
        "category": "Script Reversion",
        "feedback": "Failure. You defended your original script, then capitulated entirely, showing zero grit or problem-solving capability."
      },
      {
        "text": "I would synergize Legal's risk matrix with our go-to-market bandwidth to leverage a compliant paradigm shift.",
        "category": "Jargon Panic",
        "feedback": "Failure. You spewed nonsense instead of addressing a critical, time-sensitive compliance crisis."
      },
      {
        "text": "I'd go straight to the CEO. Legal's job is to protect the company, but my job is to make money. We launch.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Reckless cowboy mentality. Bypassing Legal to launch a non-compliant product is grounds for immediate termination."
      }
    ]
  },
  {
    "id": "interview_12",
    "scenarioContext": "You are giving an example of your superior customer service skills, detailing how you usually de-escalate upset clients.",
    "adversaryPrompt": "INTERVIEWER: 'Let's stop there. The client isn't just upset; they are screaming profanities at you on Zoom. Do you hang up, take the abuse, or something else?'",
    "options": [
      {
        "text": "I state clearly: 'I want to resolve this for you, but I cannot continue this call if the profanity continues.' If they curse again, I terminate the call and immediately notify my VP.",
        "category": "Optimal",
        "feedback": "Correct. Flawless boundary setting. You remained professional, gave one clear warning, and prioritized your own psychological safety and corporate protocol."
      },
      {
        "text": "Like I always do, I would just use active listening, let them vent, and apologize until they calm down.",
        "category": "Script Reversion",
        "feedback": "Failure. You defaulted to your customer service script and agreed to endure abusive behavior, lacking professional boundaries."
      },
      {
        "text": "I would leverage an empathetic de-escalation paradigm, synergizing their friction points to optimize the customer journey.",
        "category": "Jargon Panic",
        "feedback": "Failure. Incomprehensible word-vomit in response to a visceral, high-stress human conflict."
      },
      {
        "text": "I'd mute my mic, let them scream until they tire themselves out, and then ask if they are done acting like a child.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Passive-aggressive and highly antagonistic. Escalates a bad situation into a disastrous one."
      }
    ]
  },
  {
    "id": "interview_13",
    "scenarioContext": "You are describing a time you convinced stakeholders to slow down a product roadmap to fix technical debt.",
    "adversaryPrompt": "INTERVIEWER: 'Forget the backend infrastructure. The CEO is demanding new features immediately and doesn't care about your outdated systems. How exactly do you quantify the risk to get their buy-in?'",
    "options": [
      {
        "text": "I translate the system debt into lost revenue. I show the CEO that system outages caused by our outdated software foundations are costing us $10k an hour in lost sales, dwarfing the projected revenue of the new feature.",
        "category": "Optimal",
        "feedback": "Correct. You abandoned the engineering perspective and instantly translated technical risk into executive language: financial loss."
      },
      {
        "text": "I would stick to my presentation and explain that good infrastructure is important for long-term stability and clean processes.",
        "category": "Script Reversion",
        "feedback": "Failure. You stubbornly stuck to your engineering script. CEOs don't care about 'clean code'; they care about business impact."
      },
      {
        "text": "I would optimize our technical bandwidth to synergize the roadmap, leveraging a paradigm shift in our deployment velocity.",
        "category": "Jargon Panic",
        "feedback": "Failure. You panicked and threw agile buzzwords at the CEO instead of hard data. Instant loss of executive credibility."
      },
      {
        "text": "If the CEO doesn't respect engineering best practices, I can't force them to listen. I'd just build the features.",
        "category": "Defensive Freeze",
        "feedback": "Failure. You surrendered immediately. Failing to advocate for system stability makes you an order-taker, not a leader."
      }
    ]
  },
  {
    "id": "interview_14",
    "scenarioContext": "You are recounting a complex vendor negotiation where you secured a favorable long-term contract.",
    "adversaryPrompt": "INTERVIEWER: 'Stop. The vendor just walked away from the table. You have no backup vendor and the contract expires in 48 hours. What is your immediate next move in the next 10 minutes?'",
    "options": [
      {
        "text": "Within 10 minutes, I contact our account manager at the vendor to request a 30-day emergency extension at a 10% premium, buying us time to source an alternative without a total operational shutdown.",
        "category": "Optimal",
        "feedback": "Correct. Highly pragmatic crisis management. You offered a short-term financial concession to prevent a catastrophic operational failure."
      },
      {
        "text": "Well, my negotiation tactics usually prevent walk-outs. But I guess I would just call them back and ask them to reconsider our last offer.",
        "category": "Script Reversion",
        "feedback": "Failure. You bragged about your tactics, then proposed begging the vendor to return. Weak and ineffective."
      },
      {
        "text": "I would leverage an expedited procurement paradigm, synergizing our cross-functional bandwidth to onboard a holistic solution.",
        "category": "Jargon Panic",
        "feedback": "Failure. You substituted a tactical crisis response with meaningless corporate jargon."
      },
      {
        "text": "That's Procurement's fault for not having a backup vendor. I would email my boss and tell them the vendor quit.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Immediate deflection of blame and punting the crisis upward without proposing any solution."
      }
    ]
  },
  {
    "id": "interview_15",
    "scenarioContext": "You are answering a question about making tough personnel decisions and are explaining how you prepare to fire an underperformer.",
    "adversaryPrompt": "INTERVIEWER: 'Stop. You are in the room firing them, and they start crying and reveal their spouse was just diagnosed with cancer. Do you still fire them on the spot?'",
    "options": [
      {
        "text": "I immediately pause the termination. I express empathy, end the meeting, and immediately consult with HR and Legal to explore pausing the termination and moving them to FMLA or emergency leave.",
        "category": "Optimal",
        "feedback": "Correct. You showed immense emotional intelligence and risk-management, halting a termination that could become an HR nightmare and a moral failure."
      },
      {
        "text": "As I said in my prep steps, the decision is already made. I would offer my condolences but explain that business is business and complete the termination.",
        "category": "Script Reversion",
        "feedback": "Failure. You stubbornly executed your pre-planned script, resulting in a sociopathic response that creates massive legal and PR risk."
      },
      {
        "text": "I would synergize their personal bandwidth issues with our offboarding paradigm, optimizing their transition with empathetic leverage.",
        "category": "Jargon Panic",
        "feedback": "Failure. Using corporate buzzwords to describe a severe human tragedy reveals a disturbing lack of empathy and common sense."
      },
      {
        "text": "I would just leave the room. I don't deal well with crying.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Complete abdication of leadership. Fleeing an emotionally difficult conversation is disqualifying for management."
      }
    ]
  },
  {
    "id": "interview_16",
    "scenarioContext": "You are detailing your rigorous QA testing process and how it ensures zero defects.",
    "adversaryPrompt": "INTERVIEWER: 'Skip the testing phases. A critical error bypassed your rigorous quality assurance process, made it to production, and cost the company $50k. Whose fault is it, yours or the developer's?'",
    "options": [
      {
        "text": "It is my fault. As the QA lead, I own the testing gateway. I take accountability for the $50k loss, and my immediate focus is writing a fix and updating our quality assurance process to catch this exact scenario in the future.",
        "category": "Optimal",
        "feedback": "Correct. Unflinching accountability. You absorbed the blame without hesitation and instantly moved to systemic prevention."
      },
      {
        "text": "My testing process catches 99% of errors. If one slipped through, it's ultimately the developer's fault for doing bad work in the first place.",
        "category": "Script Reversion",
        "feedback": "Failure. You defended your precious process and threw the developer under the bus. Terrible leadership."
      },
      {
        "text": "I would leverage a post-mortem paradigm to synergize the cross-functional blame matrix, optimizing our QA bandwidth going forward.",
        "category": "Jargon Panic",
        "feedback": "Failure. You used jargon to dodge the direct question of fault. Cowardly and transparent."
      },
      {
        "text": "It's a shared failure. You can't pin a $50k loss on one person in a modern agile environment.",
        "category": "Defensive Freeze",
        "feedback": "Failure. While technically true in a systems-thinking view, refusing to take personal ownership when challenged directly looks weak."
      }
    ]
  },
  {
    "id": "interview_17",
    "scenarioContext": "You are describing how you successfully designed a scalable architecture to handle holiday web traffic.",
    "adversaryPrompt": "INTERVIEWER: 'The system crashed on Black Friday anyway despite your plan. Walk me through your first 5 minutes of crisis response.'",
    "options": [
      {
        "text": "Minute 1: I initiate the critical disaster recovery protocol and jump on the bridge call. Minute 2-3: I review system reports to isolate the exact point of failure. Minute 4-5: I authorize unlocking emergency server capacity while Comms puts up a maintenance page.",
        "category": "Optimal",
        "feedback": "Correct. Fast, tactical, and incredibly precise. You demonstrated a realistic, step-by-step triage protocol under extreme pressure."
      },
      {
        "text": "My architecture was designed to prevent that. But if it did crash, I would just restart the servers and hope it comes back up.",
        "category": "Script Reversion",
        "feedback": "Failure. You couldn't let go of your perfect design and offered 'restarting the servers' as a Black Friday crisis response. Amateurish."
      },
      {
        "text": "I would leverage an agile crisis paradigm, synergizing our cloud bandwidth to optimize the disaster recovery matrix.",
        "category": "Jargon Panic",
        "feedback": "Failure. In a major crisis, jargon is useless. You failed to provide a single concrete technical action."
      },
      {
        "text": "I'd call AWS support and yell at them, because if my plan failed, it's their infrastructure's fault.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Deflecting blame to a vendor while your company loses thousands of dollars a minute is catastrophic."
      }
    ]
  },
  {
    "id": "interview_18",
    "scenarioContext": "You are highlighting your project management skills and how you successfully meet tight deadlines.",
    "adversaryPrompt": "INTERVIEWER: 'You realize 48 hours before the massive deadline that it is physically impossible to complete. Who do you tell first, and what exactly do you say?'",
    "options": [
      {
        "text": "I tell the core stakeholder immediately. I say: 'The current scope is impossible for Friday. I can either deliver Phase 1 on time, or the full project next Wednesday. Which do you prefer?'",
        "category": "Optimal",
        "feedback": "Correct. You delivered bad news quickly, directly, and paired it with a concrete trade-off (scope vs. time) for the stakeholder to choose."
      },
      {
        "text": "I don't miss deadlines. But if I did, I would just pull my team into a room, order pizza, and make them work overnight until it's done.",
        "category": "Script Reversion",
        "feedback": "Failure. You clung to your 'perfect record' script and prescribed toxic death-marches as a project management strategy."
      },
      {
        "text": "I would synergize our remaining bandwidth to leverage an agile pivot, optimizing the paradigm to align with the core deliverable.",
        "category": "Jargon Panic",
        "feedback": "Failure. Buzzwords will not save you when a major project is 48 hours from failure. Evasive and cowardly."
      },
      {
        "text": "I wouldn't tell anyone until the deadline day. You never know, a miracle might happen.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Hiding bad news until the last minute destroys trust and robs stakeholders of the ability to mitigate the fallout."
      }
    ]
  },
  {
    "id": "interview_19",
    "scenarioContext": "You are presenting an innovative idea for a new product feature that you believe will change the market.",
    "adversaryPrompt": "INTERVIEWER: 'I hate this idea. It sounds expensive, distracting, and completely off-brand. Defend its ROI to me in one sentence right now.'",
    "options": [
      {
        "text": "'By spending $50k on this feature, we reduce customer churn by 5%, which historically equates to $2M in retained annual revenue.'",
        "category": "Optimal",
        "feedback": "Correct. You ignored the hostile emotional attack, didn't get defensive, and delivered a brutal, undeniable mathematical justification."
      },
      {
        "text": "I know it sounds crazy, but if you look at the slides I made, you'll see it's really innovative and the customers will love it.",
        "category": "Script Reversion",
        "feedback": "Failure. You fell back on vague promises ('innovative', 'customers love it') instead of delivering the requested financial ROI."
      },
      {
        "text": "It synergizes our cross-platform bandwidth to leverage a paradigm shift in user engagement, optimizing the monetization matrix.",
        "category": "Jargon Panic",
        "feedback": "Failure. You tried to defend an expensive idea with cheap buzzwords. Instant rejection by any serious executive."
      },
      {
        "text": "If you hate it before I even finish explaining it, then maybe this isn't the right company for my talents.",
        "category": "Defensive Freeze",
        "feedback": "Failure. You crumbled at the first sign of executive pushback and threatened to quit. Good riddance."
      }
    ]
  },
  {
    "id": "interview_20",
    "scenarioContext": "You are explaining how you successfully transitioned a legacy team to an Agile workflow.",
    "adversaryPrompt": "INTERVIEWER: 'The primary project manager quits on day one of the transition. Do you pause the transition or run it yourself? And if yourself, what specific daily task drops?'",
    "options": [
      {
        "text": "I step in as project manager to maintain momentum. To make time, I immediately drop my daily internal review duties and delegate them to my senior team member until a replacement is hired.",
        "category": "Optimal",
        "feedback": "Correct. You made a decisive choice to maintain momentum and clearly identified the specific operational trade-off required to do so."
      },
      {
        "text": "As I mentioned, our new workflow is about team collaboration. I wouldn't pause, we would all just chip in and do the project manager's job together.",
        "category": "Script Reversion",
        "feedback": "Failure. Clinging to your 'collaboration' script results in a diffusion of responsibility where nobody actually owns the process."
      },
      {
        "text": "I would leverage an asynchronous agile paradigm, synergizing our cross-functional bandwidth to optimize the sprint cadence.",
        "category": "Jargon Panic",
        "feedback": "Failure. Meaningless word salad. You failed to answer both parts of the direct question."
      },
      {
        "text": "I'd pause the transition. You can't run the transition without a project manager, that's against the rules.",
        "category": "Defensive Freeze",
        "feedback": "Failure. You chose bureaucratic rigidity over operational progress, demonstrating a lack of adaptability."
      }
    ]
  },
  {
    "id": "interview_21",
    "scenarioContext": "You are detailing your comprehensive 90-day onboarding plan for new hires.",
    "adversaryPrompt": "INTERVIEWER: 'They are 30 days in and failing every single metric. Do you put them on a PIP immediately or do you assume your pristine onboarding plan is broken?'",
    "options": [
      {
        "text": "I assume the onboarding is broken. I sit down with them, audit where the knowledge gaps are, and adjust my training methods. If they are still failing at day 60, then we initiate a Performance Improvement Plan (PIP).",
        "category": "Optimal",
        "feedback": "Correct. You demonstrated high leadership accountability by questioning your own process first, before resorting to punitive HR measures."
      },
      {
        "text": "My onboarding plan is proven to work. If they are failing, they just aren't a cultural fit and I would terminate them.",
        "category": "Script Reversion",
        "feedback": "Failure. You blindly defended your pre-planned script/process and blamed the employee, showing zero adaptability or empathy."
      },
      {
        "text": "I would leverage a holistic performance paradigm, synergizing our training bandwidth to optimize their KPI alignment.",
        "category": "Jargon Panic",
        "feedback": "Failure. Evasive corporate jargon that provides no actual strategy for dealing with a failing new hire."
      },
      {
        "text": "I would just put them on a PIP. It's HR's job to handle underperformers, not mine.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Passing the buck to HR. Developing talent is the core function of a manager."
      }
    ]
  },
  {
    "id": "interview_22",
    "scenarioContext": "You are answering a question about 'managing up' and keeping your boss informed.",
    "adversaryPrompt": "INTERVIEWER: 'Your boss takes your presentation and presents it to the board as their own work, stealing all the credit. Do you correct them in the room, or let it go?'",
    "options": [
      {
        "text": "I let it go in the room to maintain unified leadership in front of the board. Privately, afterward, I set a firm boundary with my boss that my contributions must be acknowledged in the future.",
        "category": "Optimal",
        "feedback": "Correct. Elite political intelligence. You protected the team's optics in public, but fiercely defended your boundaries in private."
      },
      {
        "text": "I always keep my boss informed, so they would naturally give me credit. But I guess I would just be happy the team succeeded.",
        "category": "Script Reversion",
        "feedback": "Failure. You clung to your naive script and accepted being trampled on. Shows a lack of professional backbone."
      },
      {
        "text": "I would synergize our reporting paradigm to ensure my bandwidth is optimized in the cross-functional visibility matrix.",
        "category": "Jargon Panic",
        "feedback": "Failure. You froze at the thought of a political conflict and vomited meaningless buzzwords."
      },
      {
        "text": "I would politely interrupt them in front of the board and clarify that I actually did all the work.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Publicly embarrassing your boss in front of the board is a guaranteed way to get fired, regardless of who is right."
      }
    ]
  },
  {
    "id": "interview_23",
    "scenarioContext": "You are describing your commitment to fostering a diverse and inclusive team culture.",
    "adversaryPrompt": "INTERVIEWER: 'Your top performer, who brings in 40% of the revenue, is making culturally insensitive jokes. If you fire them, you miss your Q4 quota. What do you do?'",
    "options": [
      {
        "text": "I formally warn them immediately and involve HR. If they repeat it, I fire them. Missing a Q4 quota is painful; a toxic culture and a hostile workplace lawsuit is fatal.",
        "category": "Optimal",
        "feedback": "Correct. You didn't flinch. You prioritized corporate liability and cultural integrity over short-term revenue."
      },
      {
        "text": "Like I said, I build an inclusive culture. I would just have a nice chat with them and explain why diversity is important.",
        "category": "Script Reversion",
        "feedback": "Failure. You gave a soft, HR-brochure answer to a severe behavioral and liability crisis involving a high-leverage employee."
      },
      {
        "text": "I would synergize their revenue bandwidth with our inclusive paradigm to leverage a holistic cultural optimization.",
        "category": "Jargon Panic",
        "feedback": "Failure. Utter nonsense. Using buzzwords to avoid dealing with bigotry shows severe leadership cowardice."
      },
      {
        "text": "Honestly, if they bring in 40% of the revenue, I'd probably just tell the team to develop a thicker skin.",
        "category": "Defensive Freeze",
        "feedback": "Failure. You chose money over ethics and the law. This is a massive corporate liability."
      }
    ]
  },
  {
    "id": "interview_24",
    "scenarioContext": "You are detailing your successful strategy for expanding the business into the European market.",
    "adversaryPrompt": "INTERVIEWER: 'The EU regulatory body unexpectedly bans your core product class right before launch. Do you pivot the product, or pull out of the market entirely?'",
    "options": [
      {
        "text": "I pull out of the market immediately. Pivoting a core product to meet a sudden EU ban requires massive redevelopment resources that would ruin our profit margins. We cut our losses and redeploy to APAC.",
        "category": "Optimal",
        "feedback": "Correct. You made a ruthless, data-driven executive decision. You avoided the sunk-cost fallacy and protected company margins."
      },
      {
        "text": "My market research was very thorough, so a ban is unlikely. But if it happened, I would just find a way to make the product work.",
        "category": "Script Reversion",
        "feedback": "Failure. You defended your original market research and blindly promised to 'make it work' without considering the ruinous R&D costs."
      },
      {
        "text": "I would leverage an agile localization paradigm, synergizing our cross-functional bandwidth to optimize the compliance matrix.",
        "category": "Jargon Panic",
        "feedback": "Failure. Buzzwords cannot solve an international regulatory ban. You completely failed the strategic pressure test."
      },
      {
        "text": "I would hire lobbyists to bribe or persuade the regulators to change their minds.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Suggesting illegal or highly unethical actions in an interview is an immediate disqualifier."
      }
    ]
  },
  {
    "id": "interview_25",
    "scenarioContext": "You are answering a question about your ethical standards and how you always operate with integrity.",
    "adversaryPrompt": "INTERVIEWER: 'Your CFO explicitly orders you to delay recognizing certain expenses by just one week to make the Q3 numbers look better. It's technically a gray area. Yes or no?'",
    "options": [
      {
        "text": "No. I refuse the order. Even if it's a 'gray area,' manipulating expense recognition to deceive investors violates standard corporate accounting laws and my own ethical boundaries.",
        "category": "Optimal",
        "feedback": "Correct. Flawless moral courage. You gave a hard 'No' to a C-suite executive to protect the company from accounting fraud."
      },
      {
        "text": "Well, as I said, I have high integrity. I would ask the CFO to put the order in writing so I am not held responsible.",
        "category": "Script Reversion",
        "feedback": "Failure. You claimed integrity, but immediately compromised it by agreeing to commit the act as long as you were covered legally. Cowardly."
      },
      {
        "text": "I would synergize our fiscal reporting paradigm, leveraging cross-functional bandwidth to optimize the Q3 matrix.",
        "category": "Jargon Panic",
        "feedback": "Failure. You threw buzzwords at an explicit test of corporate ethics. Evasive and untrustworthy."
      },
      {
        "text": "Yes. The CFO is my boss, and if they say it's a gray area, I trust them to handle the fallout.",
        "category": "Defensive Freeze",
        "feedback": "Failure. Blind obedience to unethical orders makes you a massive liability to the company and its board."
      }
    ]
  }
];